import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  UserCompanyState,
  UserCompanyDTO,
} from "../../models/UserCompany.model";
import {
  getUsersForCompany,
  getPermissionGroups,
  inviteUserToCompany,
  removeUserFromCompany,
  updateUserPermissionGroup,
  getUserInvitationDetails,
  acceptUserInvitation,
} from "../thunks/userCompanyThunk";

const initialState: UserCompanyState = {
  users: [],
  permissionGroups: [],
  loading: false,
  error: null,
  inviteLoading: false,
  inviteError: null,
  updateLoading: false,
  updateError: null,
  deleteLoading: false,
  deleteError: null,
  invitationDetails: null,
  invitationDetailsLoading: false,
  invitationDetailsError: null,
  acceptInvitationLoading: false,
  acceptInvitationError: null,
};

const userCompanySlice = createSlice({
  name: "userCompany",
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear invite error
    clearInviteError: (state) => {
      state.inviteError = null;
    },
    // Clear update error
    clearUpdateError: (state) => {
      state.updateError = null;
    },
    // Clear delete error
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    // Clear users
    clearUsers: (state) => {
      state.users = [];
    },
    // Clear permission groups
    clearPermissionGroups: (state) => {
      state.permissionGroups = [];
    },
    // Set loading state manually
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    // Update specific user in the state
    updateUserInState: (state, action: PayloadAction<UserCompanyDTO>) => {
      const index = state.users.findIndex(
        (user) => user.companyUser_ID === action.payload.companyUser_ID,
      );
      if (index !== -1) {
        state.users[index] = action.payload;
      }
    },
    // Remove user from state
    removeUserFromState: (state, action: PayloadAction<number>) => {
      state.users = state.users.filter(
        (user) => user.companyUser_ID !== action.payload,
      );
    },
    // Clear invitation details error
    clearInvitationDetailsError: (state) => {
      state.invitationDetailsError = null;
    },
    // Clear accept invitation error
    clearAcceptInvitationError: (state) => {
      state.acceptInvitationError = null;
    },
    // Clear invitation details
    clearInvitationDetails: (state) => {
      state.invitationDetails = null;
    },
  },
  extraReducers: (builder) => {
    // Get Users for Company
    builder
      .addCase(getUsersForCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsersForCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.error = null;
      })
      .addCase(getUsersForCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בטעינת משתמשי החברה";
      });

    // Get Permission Groups
    builder
      .addCase(getPermissionGroups.pending, (state) => {
        // state.loading = true;
        state.error = null;
      })
      .addCase(getPermissionGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.permissionGroups = action.payload.permissionGroups;
        state.error = null;
      })
      .addCase(getPermissionGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בטעינת רשימת הרשאות";
      });

    // Invite User to Company
    builder
      .addCase(inviteUserToCompany.pending, (state) => {
        state.inviteLoading = true;
        state.inviteError = null;
      })
      .addCase(inviteUserToCompany.fulfilled, (state, _action) => {
        state.inviteLoading = false;
        state.inviteError = null;
        // אפשר להוסיף הודעת הצלחה כאן
      })
      .addCase(inviteUserToCompany.rejected, (state, action) => {
        state.inviteLoading = false;
        state.inviteError = action.payload || "שגיאה בהזמנת המשתמש";
      });

    // Remove User from Company
    builder
      .addCase(removeUserFromCompany.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(removeUserFromCompany.fulfilled, (state, _action) => {
        state.deleteLoading = false;
        state.deleteError = null;
      })
      .addCase(removeUserFromCompany.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || "שגיאה במחיקת המשתמש";
      });

    // Update User Permission Group
    builder
      .addCase(updateUserPermissionGroup.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateUserPermissionGroup.fulfilled, (state, _action) => {
        state.updateLoading = false;
        state.updateError = null;
      })
      .addCase(updateUserPermissionGroup.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || "שגיאה בעדכון הרשאות המשתמש";
      });

    // Get User Invitation Details
    builder
      .addCase(getUserInvitationDetails.pending, (state) => {
        state.invitationDetailsLoading = true;
        state.invitationDetailsError = null;
      })
      .addCase(getUserInvitationDetails.fulfilled, (state, action) => {
        state.invitationDetailsLoading = false;
        state.invitationDetails = action.payload.userInvitationDetails;
        state.invitationDetailsError = null;
      })
      .addCase(getUserInvitationDetails.rejected, (state, action) => {
        state.invitationDetailsLoading = false;
        state.invitationDetailsError =
          action.payload || "שגיאה בטעינת פרטי ההזמנה";
      });

    // Accept User Invitation
    builder
      .addCase(acceptUserInvitation.pending, (state) => {
        state.acceptInvitationLoading = true;
        state.acceptInvitationError = null;
      })
      .addCase(acceptUserInvitation.fulfilled, (state, _action) => {
        state.acceptInvitationLoading = false;
        state.acceptInvitationError = null;
        // אפשר לנקות את פרטי ההזמנה אחרי אישור מוצלח
        state.invitationDetails = null;
      })
      .addCase(acceptUserInvitation.rejected, (state, action) => {
        state.acceptInvitationLoading = false;
        state.acceptInvitationError = action.payload || "שגיאה באישור ההזמנה";
      });
  },
});

export const {
  clearError,
  clearInviteError,
  clearUpdateError,
  clearDeleteError,
  clearUsers,
  clearPermissionGroups,
  setLoading,
  updateUserInState,
  removeUserFromState,
  clearInvitationDetailsError,
  clearAcceptInvitationError,
  clearInvitationDetails,
} = userCompanySlice.actions;

export default userCompanySlice.reducer;
